import {
    Box,
    Button,
    Flex,
    FormControl,
    FormErrorMessage,
    FormLabel,
    Input,
    Text,
    VStack,
    useColorModeValue
} from '@chakra-ui/react';
import { v4 } from 'uuid';
import { translate } from '../../../i18n';
import { CoachPricingRuleDto } from '../../../models/coach/dto/coach-pricing-rule.dto';
import { CoachDto } from '../../../models/coach/dto/coach.dto';
import SportPicker from '../../court-management/components/SportPicker';
import { getDefaultCurrency } from '../../court-pricing-management/components/CurrencyPicker';
import DurationInput from '../../court-pricing-management/components/DurationInput';
import PriceInput, { isPriceInputValid } from '../../court-pricing-management/components/PriceInput';

type Props = {
    title: string;
    coach: CoachDto;
    pricingRules: CoachPricingRuleDto[];
    setPricingRules: (newRules: CoachPricingRuleDto[]) => void;
};

export default function CoachPricingEditor({ title, coach, pricingRules, setPricingRules }: Props) {
    const handleAddRule = () => {
        const newRule: CoachPricingRuleDto = {
            id: v4(),
            hourlyFees: pricingRules.length > 0 ? pricingRules[pricingRules.length - 1].hourlyFees : 10,
            currency: getDefaultCurrency(),
            coachId: coach.id,
            participantCount: 1,
            sport: coach.sports[0],
            priceDuration: 3_600_000,
        };
        setPricingRules([...pricingRules, newRule]);
    };

    const headerBg = useColorModeValue('gray.200', 'gray.600');

    const handleUpdateRule = (index: number, field: keyof CoachPricingRuleDto, value: CoachPricingRuleDto[keyof CoachPricingRuleDto]) => {
        const updatedRules = [...pricingRules];
        updatedRules[index] = {
            ...updatedRules[index],
            [field]: value,
        };
        setPricingRules([...updatedRules]);
    };

    const handleDeleteRule = (index: number) => {
        const updatedRules = pricingRules.filter((_, i) => i !== index);
        setPricingRules(updatedRules);
    };
    const labelHeight = '36px';
    return (
        <VStack align="stretch">
            <Box bg={headerBg} mx={2} py={2} px={2} borderRadius={8} display='flex' flexDirection={{ base: 'column', sm: 'row' }} alignItems={{ base: 'stretch', sm: 'center' }} justifyContent={'space-between'} gap={2} minWidth={0}>
                <Text fontWeight={'bold'} overflow="hidden" textOverflow="ellipsis" whiteSpace="nowrap">
                    {title}
                </Text>
                <Box display='flex' flexDirection={'row'} justifyContent={'flex-end'} gap={4} flexShrink={0}>
                    <Button size={'xs'} onClick={() => handleAddRule()}>
                        +
                    </Button>
                    <Button size={'xs'} isDisabled={pricingRules.length < 2} onClick={() => {
                        if (pricingRules.length > 1) {
                            handleDeleteRule(pricingRules.length - 1);
                        }
                    }}>
                        -
                    </Button>
                </Box>
            </Box>
            {pricingRules.map((rule, index) => (
                <Flex
                    key={rule.id}
                    align="start"
                    gap={4}
                    paddingLeft={4}
                    paddingRight={4}
                    mb={4}
                    direction={{ base: 'column', md: 'row' }}
                    flexWrap="wrap"
                    minWidth={0}
                >
                    <FormControl isInvalid={!Number.isInteger(rule.participantCount)} minW={{ base: '100%', md: '120px' }} flex={{ base: '1 1 100%', md: '1 1 0' }}>
                        <FormLabel h={labelHeight}>{translate('coachPricing.number_of_allowed_participants')}</FormLabel>
                        <Input
                            type="number"
                            value={rule.participantCount}
                            onChange={(event) => handleUpdateRule(index, 'participantCount', parseInt(event.target.value, 10))}
                        />
                        <FormErrorMessage>{translate('form.must_be_number')}</FormErrorMessage>
                    </FormControl>
                    <FormControl minW={{ base: '100%', md: '140px' }} flex={{ base: '1 1 100%', md: '1 1 0' }}>
                        <FormLabel h={labelHeight}>{translate('coachPricing.sport')}</FormLabel>
                        <SportPicker
                            sport={rule.sport}
                            setSport={(sport) => handleUpdateRule(index, 'sport', sport[0])}
                        />
                    </FormControl>
                    <FormControl isInvalid={!isPriceInputValid(rule.hourlyFees.toString())} minW={{ base: '100%', md: '120px' }} flex={{ base: '1 1 100%', md: '1 1 0' }}>
                        <FormLabel h={labelHeight}>{translate('coachPricing.hourly_fees')}</FormLabel>
                        <PriceInput
                            name="court_pricing"
                            value={rule.hourlyFees}
                            onChange={(event) => handleUpdateRule(index, 'hourlyFees', event.target.value)}
                            currency={rule.currency}
                            setCurrency={(currency) => {
                                handleUpdateRule(index, 'currency', currency);
                            }}
                        />
                        <Box h={4} display={'flex'} alignItems={'start'}>
                            <FormErrorMessage>{translate('invalid_pricing')}</FormErrorMessage>
                        </Box>
                    </FormControl>
                    <FormControl minW={{ base: '100%', md: '120px' }} flex={{ base: '1 1 100%', md: '1 1 0' }}>
                        <FormLabel h={labelHeight}>{translate('coachs.price_duration')}</FormLabel>
                        <DurationInput
                            durationInMs={rule.priceDuration}
                            setDuration={(newPriceDuration) => handleUpdateRule(index, 'priceDuration', newPriceDuration)}
                        />
                    </FormControl>
                </Flex>
            ))}
        </VStack>
    );
}
